import React, { FormEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import * as T from '^/types';
import QuestionSVG from '^/assets/icons/contents-list/question-mark.svg';

import { UseState, useL10n } from '^/hooks';
import Text from './text';
import LockSvg from '^/assets/icons/gizmo/lock.svg';
import palette from '^/constants/palette';
import DDMInput from '^/components/atoms/DDMInput/1';
import Tippy from '@tippyjs/react';
import { makeV2APIURL } from '^/store/duck/API';
import { useSearchParamsQuery } from '^/hooks/useSearchParamsQuery';
import { L10nDictionary } from '^/utilities/l10n';
import Button from '^/components/atoms/Button';

const FormWrapper = styled.li<{ show: boolean }>(({ show }) => ({
  opacity: show ? 1 : 0,
  maxHeight: show ? 'auto' : 0,
  overflow: 'hidden',
  transition: 'opacity 0.3s ease, max-height 0.3s ease',

  ':not(:first-of-type)': {
    marginTop: '20px',
  },
}));

const Label = styled.label({
  display: 'flex',
  marginBottom: '5px',
  marginTop: '10px',
  gap: '8px',

  fontSize: '13px',
  fontWeight: 500,
  color: palette.darkBlack.toString(),
});

const ErrorMessage = styled.p({
  fontSize: '13px',
  lineHeight: 1.3,
  margin: '10px 0',
  color: palette.error.toString(),
  textAlign: 'left',
});

const LockIcon = styled(LockSvg)({
  marginRight: '15px',
});

export default function SAMLSSOButton() {
  const [l10n] = useL10n();
  const [show, setShow] = useState(false);
  const [teamSlug, setTeamSlug]: UseState<string> = useState('');
  const [errorMessage, setErrorMessage] = useState<L10nDictionary | null>(null);
  const searchParams = useSearchParamsQuery();

  const initializeSignIn = async (e: FormEvent) => {
    e.preventDefault();

    setShow(true);

    if (!teamSlug && show) {
      setErrorMessage(Text.errorMessage);
    } else if (teamSlug && show) {
      // TODO: Interceptors, change it with http
      const url = makeV2APIURL('auth', 'saml', 'init', `${teamSlug}`);
      const response = await fetch(url, { redirect: 'manual' });
      if (response.status === 0) {
        window.location.replace(url);
      } else {
        setErrorMessage(Text.notFoundErrorMessage);
      }
    }
  };

  const handleTeamSlugChange: (event: SyntheticEvent<HTMLInputElement>) => void = useCallback(
    event => {
      setTeamSlug(event.currentTarget.value.toLowerCase());
      setErrorMessage(null);
    },
    []
  );

  const handleErrorMessage = (errorCode: T.HTTPError) => {
    switch (errorCode) {
      case T.HTTPError.CLIENT_UNAUTHORIZED_ERROR:
        return Text.unauthorizedErrorMessage;
      case T.HTTPError.CLIENT_UNPROCESSABLE_ENTITY_ERROR:
        return Text.unprocessableErrorMessage;
      default:
        return Text.defaultErrorMessage;
    }
  };

  useEffect(() => {
    const hasError = searchParams.has('error');
    const errorParam = searchParams.get('error') as T.HTTPError;

    if (hasError && errorParam) {
      setShow(true);
      const errMessage = handleErrorMessage(errorParam);
      setErrorMessage(errMessage);
    }
  }, [searchParams]);

  const errorComponent = errorMessage ? (
    <ErrorMessage data-testid="saml-sign-up-input-field-error">* {l10n(errorMessage)}</ErrorMessage>
  ) : null;

  const kind = 'text';
  const id: string = `saml-sign-in-field-${kind}`;

  const label = (
    <Label htmlFor={id}>
      {l10n(Text.teamSlug)}
      <Tippy
        offset={T.TIPPY_OFFSET}
        theme="angelsw"
        placement="right"
        arrow={false}
        content={l10n(Text.tooltip)}
      >
        <span>
          <QuestionSVG />
        </span>
      </Tippy>
    </Label>
  );

  return (
    <form onSubmit={initializeSignIn} style={{ width: '100%' }}>
      <ul>
        <FormWrapper id="saml-form" show={show}>
          {label}
          <DDMInput
            id={id}
            value={teamSlug}
            name={kind}
            type={kind}
            placeholder="my-organization"
            error={Boolean(errorMessage)}
            onChange={handleTeamSlugChange}
          />
          {errorComponent}
        </FormWrapper>
        <Button
          type="submit"
          form="saml-form"
          variant="secondary"
          size="large"
          customStyles={{ width: '100%', margin: '20px auto 0px auto' }}
        >
          <LockIcon /> {l10n(Text.signin)}
        </Button>
      </ul>
    </form>
  );
}
